import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, reduce, switchMap } from 'rxjs/operators';

import {
  addressReviewTaskPredicate,
  buildFormTaskPredicate,
  FormKey,
  FormTask,
  ITask,
  IWorkflowInstance,
  reviewFormTaskPredicate,
  TaskStatus,
  viewReviewTaskPredicate,
} from '@assess/shared/workflow';

import { WorkflowHttpService } from './workflow-http.service';
import { IAssignDetail } from '../types/assign-detail.type';

@Injectable({ providedIn: 'root' })
export class TaskService {
  constructor(private readonly workflowHttpService: WorkflowHttpService) {}

  assignTasks(assignDetails: IAssignDetail[]): Observable<boolean> {
    return from(assignDetails).pipe(
      mergeMap((assignDetail) => {
        return this.workflowHttpService
          .assignTask(assignDetail.workflowInstanceId, assignDetail.taskId, assignDetail.taskAssignment, true)
          .pipe(
            catchError(() => {
              return this.tryUpdatedWorkflowInstance(assignDetail);
            })
          );
      }),
      map((task) => !!task),
      reduce((acc, value) => acc && value),
      catchError(() => of(false))
    );
  }

  checkoutTask(workflowInstanceId: string, taskId: string): Observable<IWorkflowInstance> {
    return this.workflowHttpService.checkoutTask(workflowInstanceId, taskId);
  }

  getAddressReviewTask(workflowTasks: ITask[]): ITask | undefined {
    return workflowTasks.find(addressReviewTaskPredicate);
  }

  getBuildFormTask(workflowTasks: ITask[]): ITask | undefined {
    return workflowTasks.find(buildFormTaskPredicate);
  }

  getReviewFormTask(workflowTasks: ITask[]): ITask | undefined {
    return workflowTasks.find(reviewFormTaskPredicate);
  }

  getViewReviewTask(workflowTasks: ITask[]): ITask | undefined {
    return workflowTasks.find(viewReviewTaskPredicate);
  }

  private tryUpdatedWorkflowInstance(assignDetail: IAssignDetail) {
    return this.workflowHttpService.getWorkflowInstance(assignDetail.workflowInstanceId).pipe(
      switchMap((workflowInstance) => {
        const task = this.findOpenTask(workflowInstance, FormTask.ReviewForm);

        if (!task) {
          return throwError('No open reviewFormTask');
        }

        return this.workflowHttpService.assignTask(
          assignDetail.workflowInstanceId,
          task['@id'],
          assignDetail.taskAssignment
        );
      })
    );
  }

  private findOpenTask(workflowInstance: IWorkflowInstance, formKey: FormKey) {
    return workflowInstance.task.find((t) => t.formKey === formKey && t.status === TaskStatus.Open);
  }
}
